import type { ReactNode } from 'react';

import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { cn } from '@/lib/ui/utils';

interface CardSectionLabelProps {
  /** Leading status glyph — trophy, gift, trade-up, etc. Omit for a text-only label. */
  icon?: IconSvgElement;
  /** Uppercase label text. */
  children: ReactNode;
  /** Optional node pinned directly right of the title (8px gap) — e.g. a live pulse dot. */
  afterTitle?: ReactNode;
  /** Optional content pinned to the right of the row (e.g. a settings popover). */
  trailing?: ReactNode;
  className?: string;
}

/**
 * The small uppercase header shared by the market-detail hero cards (Prize Pool,
 * Backer Rewards, Position): a leading secondary-tone icon plus a caption-sized
 * uppercase label, with an optional `afterTitle` slot hugging the title and an
 * optional right-aligned `trailing` slot for controls.
 */
export function CardSectionLabel({
  icon,
  children,
  afterTitle,
  trailing,
  className,
}: CardSectionLabelProps): React.ReactNode {
  return (
    <div className={cn('flex items-center gap-1', className)}>
      {icon !== undefined ? (
        <Icon icon={icon} className="size-3.5 text-foreground-secondary" aria-hidden="true" />
      ) : null}
      <div className="flex items-center gap-2">
        <span className="text-caption font-semibold uppercase tracking-tight text-foreground-secondary">
          {children}
        </span>
        {afterTitle !== undefined ? afterTitle : null}
      </div>
      {trailing !== undefined ? (
        <div className="ml-auto flex items-center gap-2">{trailing}</div>
      ) : null}
    </div>
  );
}
